<template>
  <section class="company-tracking">
    <div class="company-tracking__grid">
      <article class="company-tracking__card">
        <div class="company-tracking__head">
          <div>
            <p class="company-tracking__eyebrow">Seguimiento</p>
            <h3 class="company-tracking__title">Notas del cliente</h3>
          </div>
        </div>

        <form v-if="canManageUsers" class="company-tracking__form" @submit.prevent="submitNote">
          <textarea
            v-model="noteBody"
            class="company-tracking__textarea"
            rows="4"
            maxlength="2000"
            placeholder="Agregar observación del cliente"
          ></textarea>
          <div class="company-tracking__actions">
            <button class="company-tracking__primary" type="submit" :disabled="isSavingNote || !noteBody.trim()">
              {{ isSavingNote ? 'Guardando...' : 'Agregar nota' }}
            </button>
          </div>
        </form>

        <p v-if="noteError" class="company-tracking__error">{{ noteError }}</p>

        <div v-if="notes.length" ref="notesList" class="company-tracking__list" tabindex="0" role="region" aria-label="Notas del cliente" @scroll.passive="loadMoreOnScroll('notes', $event)">
          <article v-for="note in visibleNotes" :key="note.id" class="company-tracking__item">
            <p class="company-tracking__item-copy">{{ note.body }}</p>
            <p class="company-tracking__item-meta">
              {{ note.created_user_name || 'Usuario' }} · {{ formatDate(note.created_at) }}
            </p>
          </article>
          <button v-if="visibleNotes.length < notes.length" type="button" class="company-tracking__secondary" @click="showMore('notes')">Mostrar más notas</button>
          <p class="company-tracking__list-count" role="status">{{ visibleNotes.length }} de {{ notes.length }} notas</p>
        </div>

        <p v-else class="company-tracking__empty">No hay observaciones registradas.</p>
      </article>

      <article class="company-tracking__card">
        <div class="company-tracking__head">
          <div>
            <p class="company-tracking__eyebrow">Seguimiento</p>
            <h3 class="company-tracking__title">Tareas del cliente</h3>
          </div>
        </div>

        <form v-if="canManageUsers" class="company-tracking__form" @submit.prevent="submitTask">
          <input
            v-model="taskForm.title"
            class="input company-tracking__input"
            maxlength="200"
            type="text"
            placeholder="Título de la tarea"
          >
          <textarea
            v-model="taskForm.description"
            class="company-tracking__textarea"
            rows="4"
            maxlength="2000"
            placeholder="Detalle de la tarea"
          ></textarea>
          <div class="company-tracking__form-grid">
            <div class="select company-tracking__select">
              <select v-model="taskForm.assigned_user_id">
                <option value="">Sin asignar</option>
                <option v-for="user in users" :key="user.id" :value="String(user.id)">
                  {{ user.name }} · {{ user.email }}
                </option>
              </select>
            </div>
            <div v-if="isEditingTask" class="select company-tracking__select company-tracking__select--status">
              <select v-model="taskForm.status">
                <option value="INGRESADA">Ingresada</option>
                <option value="ASIGNADA">Asignada</option>
                <option value="TRABAJANDO">Trabajando</option>
                <option value="TERMINADA">Terminada</option>
              </select>
            </div>
          </div>
          <div class="company-tracking__actions">
            <button class="company-tracking__primary" type="submit" :disabled="isSavingTask || !taskForm.title.trim()">
              {{ isSavingTask ? 'Guardando...' : isEditingTask ? 'Guardar tarea' : 'Crear tarea' }}
            </button>
            <button v-if="isEditingTask" class="company-tracking__secondary" type="button" :disabled="isSavingTask" @click="resetTaskForm">
              Cancelar
            </button>
          </div>
        </form>

        <p v-if="taskError" class="company-tracking__error">{{ taskError }}</p>

        <div v-if="tasks.length" ref="tasksList" class="company-tracking__list" tabindex="0" role="region" aria-label="Tareas del cliente" @scroll.passive="loadMoreOnScroll('tasks', $event)">
          <article
            v-for="task in visibleTasks"
            :key="task.id"
            class="company-tracking__item company-tracking__item--task"
            :class="{ 'company-tracking__item--highlight': String(highlightedTaskId || '') === String(task.id) }"
          >
            <div class="company-tracking__task-head">
              <div>
                <p class="company-tracking__task-title">{{ task.title }}</p>
                <p v-if="task.description" class="company-tracking__item-copy">{{ task.description }}</p>
              </div>
              <span class="company-tracking__status" :class="`company-tracking__status--${task.status.toLowerCase()}`">
                {{ task.status }}
              </span>
            </div>

            <p class="company-tracking__item-meta">
              Asignado: {{ task.assigned_user_name || 'Sin asignar' }} · Creó: {{ task.created_user_name || 'Usuario' }}
            </p>
            <p class="company-tracking__item-meta">
              Actualizada: {{ formatDate(task.updated_at || task.created_at) }}
            </p>

            <div class="company-tracking__actions company-tracking__actions--task">
              <button v-if="task.task_type && task.task_type !== 'GENERAL'" class="company-tracking__secondary" type="button" @click="scrollToReview()">Ver revisión de ficha</button>
              <button
                v-if="(!task.task_type || task.task_type === 'GENERAL') && canOperateTask(task) && task.status === 'ASIGNADA'"
                class="company-tracking__secondary"
                type="button"
                :disabled="changingTaskId === task.id"
                @click="changeStatus(task, 'TRABAJANDO')"
              >
                Trabajando
              </button>
              <button
                v-if="(!task.task_type || task.task_type === 'GENERAL') && canOperateTask(task) && task.status === 'TRABAJANDO'"
                class="company-tracking__secondary"
                type="button"
                :disabled="changingTaskId === task.id"
                @click="changeStatus(task, 'TERMINADA')"
              >
                Terminada
              </button>
              <button
                v-if="(!task.task_type || task.task_type === 'GENERAL') && canManageUsers && task.status === 'TRABAJANDO'"
                class="company-tracking__secondary"
                type="button"
                :disabled="changingTaskId === task.id"
                @click="changeStatus(task, 'ASIGNADA')"
              >
                Volver a asignada
              </button>
              <button
                v-if="canManageUsers"
                class="company-tracking__secondary"
                type="button"
                :disabled="task.task_type && task.task_type !== 'GENERAL'"
                @click="editTask(task)"
              >
                Editar
              </button>
            </div>
          </article>
          <button v-if="visibleTasks.length < tasks.length" type="button" class="company-tracking__secondary" @click="showMore('tasks')">Mostrar más tareas</button>
          <p class="company-tracking__list-count" role="status">{{ visibleTasks.length }} de {{ tasks.length }} tareas</p>
        </div>

        <p v-else class="company-tracking__empty">No hay tareas registradas para este cliente.</p>
      </article>
    </div>
  </section>
</template>

<script>
import trackingService from '@/services/tracking.service'

export default {
  name: 'CompanyTrackingPanel',
  props: {
    companyId: {
      type: Number,
      required: true
    },
    notes: {
      type: Array,
      default: () => []
    },
    tasks: {
      type: Array,
      default: () => []
    },
    users: {
      type: Array,
      default: () => []
    },
    currentUserId: {
      type: Number,
      default: 0
    },
    canManageUsers: {
      type: Boolean,
      default: false
    },
    highlightedTaskId: {
      type: [String, Number],
      default: ''
    }
  },
  data () {
    return {
      noteBody: '',
      notesVisibleCount: 10,
      tasksVisibleCount: 10,
      taskForm: {
        id: null,
        title: '',
        description: '',
        assigned_user_id: '',
        status: 'INGRESADA'
      },
      isSavingNote: false,
      isSavingTask: false,
      changingTaskId: null,
      noteError: '',
      taskError: ''
    }
  },
  computed: {
    visibleNotes () {
      return this.notes.slice(0, this.notesVisibleCount)
    },
    visibleTasks () {
      return this.tasks.slice(0, this.tasksVisibleCount)
    },
    isEditingTask () {
      return Boolean(this.taskForm.id)
    }
  },
  watch: {
    companyId () {
      this.notesVisibleCount = 10
      this.tasksVisibleCount = 10
      this.$nextTick(() => {
        if (this.$refs.notesList) this.$refs.notesList.scrollTop = 0
        if (this.$refs.tasksList) this.$refs.tasksList.scrollTop = 0
        this.revealHighlightedTask()
      })
    },
    highlightedTaskId () { this.revealHighlightedTask() },
    tasks () { this.revealHighlightedTask() }
  },
  mounted () {
    this.revealHighlightedTask()
  },
  methods: {
    showMore (list) {
      const countKey = `${list}VisibleCount`
      this[countKey] = Math.min(this[countKey] + 10, this[list].length)
    },
    loadMoreOnScroll (list, event) {
      const container = event.target
      if (container.scrollHeight - container.scrollTop - container.clientHeight <= 120) {
        this.showMore(list)
      }
    },
    revealHighlightedTask () {
      if (!this.highlightedTaskId) return
      const index = this.tasks.findIndex(task => String(task.id) === String(this.highlightedTaskId))
      if (index < 0) return
      this.tasksVisibleCount = Math.max(this.tasksVisibleCount, Math.ceil((index + 1) / 10) * 10)
      this.$nextTick(() => {
        const container = this.$refs.tasksList
        const task = container && container.querySelector('.company-tracking__item--highlight')
        if (task) container.scrollTop += task.getBoundingClientRect().top - container.getBoundingClientRect().top - 4
      })
    },
    scrollToReview () {
      const panel = document.querySelector('.review-panel')
      if (!panel) return
      const disclosure = panel.querySelector('.review-panel__disclosure')
      if (disclosure) disclosure.open = true
      panel.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    formatDate (value) {
      return value ? new Date(value).toLocaleString() : ''
    },
    resetTaskForm () {
      this.taskForm = {
        id: null,
        title: '',
        description: '',
        assigned_user_id: '',
        status: 'INGRESADA'
      }
      this.taskError = ''
    },
    canOperateTask (task) {
      return this.canManageUsers || task.assigned_user_id === this.currentUserId
    },
    async submitNote () {
      this.isSavingNote = true
      this.noteError = ''
      try {
        await trackingService.createCompanyNote({
          company_id: this.companyId,
          body: this.noteBody
        })
        this.noteBody = ''
        this.$emit('refresh')
      } catch (error) {
        this.noteError = (error && error.message) || 'No fue posible guardar la nota.'
      } finally {
        this.isSavingNote = false
      }
    },
    editTask (task) {
      this.taskForm = {
        id: task.id,
        title: task.title || '',
        description: task.description || '',
        assigned_user_id: task.assigned_user_id ? String(task.assigned_user_id) : '',
        status: task.status || 'INGRESADA'
      }
      this.taskError = ''
    },
    async submitTask () {
      this.isSavingTask = true
      this.taskError = ''
      const payload = {
        id: this.taskForm.id,
        company_id: this.companyId,
        title: this.taskForm.title,
        description: this.taskForm.description,
        assigned_user_id: this.taskForm.assigned_user_id || null,
        status: this.isEditingTask ? this.taskForm.status : undefined
      }
      try {
        if (this.isEditingTask) {
          await trackingService.updateCompanyTask(payload)
        } else {
          await trackingService.createCompanyTask(payload)
        }
        this.resetTaskForm()
        this.$emit('refresh')
      } catch (error) {
        this.taskError = (error && error.message) || 'No fue posible guardar la tarea.'
      } finally {
        this.isSavingTask = false
      }
    },
    async changeStatus (task, status) {
      this.changingTaskId = task.id
      this.taskError = ''
      try {
        await trackingService.changeCompanyTaskStatus({
          id: task.id,
          status
        })
        this.$emit('refresh')
      } catch (error) {
        this.taskError = (error && error.message) || 'No fue posible actualizar la tarea.'
      } finally {
        this.changingTaskId = null
      }
    }
  }
}
</script>

<style scoped>
.company-tracking__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
  align-items: start;
}

.company-tracking__card {
  padding: 1rem;
  border: 1px solid rgba(110, 91, 66, 0.08);
  border-radius: 26px;
  background: linear-gradient(180deg, rgba(255, 252, 247, 0.92), rgba(246, 239, 231, 0.86));
}

.company-tracking__head {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.8rem;
}

.company-tracking__eyebrow {
  margin: 0 0 0.3rem;
  font-size: 0.68rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(58, 46, 33, 0.46);
}

.company-tracking__title {
  margin: 0;
  color: #211913;
}

.company-tracking__form {
  display: grid;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.company-tracking__input,
.company-tracking__textarea,
.company-tracking__select select {
  width: 100%;
  border-radius: 18px;
  border: 1px solid rgba(102, 81, 57, 0.16);
  background: rgba(255, 255, 255, 0.82);
  color: #2d231a;
}

.company-tracking__input,
.company-tracking__select select {
  min-height: 2.9rem;
}

.company-tracking__textarea {
  padding: 0.85rem 0.95rem;
  resize: vertical;
}

.company-tracking__select--status select {
  font-size: 0.75rem;
}

.company-tracking__form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.company-tracking__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
}

.company-tracking__primary,
.company-tracking__secondary {
  min-height: 2.55rem;
  padding: 0 0.95rem;
  border-radius: 999px;
}

.company-tracking__primary {
  border: 0;
  background: linear-gradient(135deg, #241c15 0%, #4b3a29 100%);
  color: #f7f0e5;
}

.company-tracking__secondary {
  border: 1px solid rgba(104, 83, 57, 0.14);
  background: rgba(255, 255, 255, 0.72);
  color: #352b21;
}

.company-tracking__list {
  display: grid;
  gap: 0.75rem;
  max-height: min(60vh, 36rem);
  overflow-y: auto;
  overscroll-behavior-y: contain;
  scrollbar-gutter: stable;
  padding: 4px;
  border-radius: 18px;
  scrollbar-width: thin;
  scrollbar-color: #c5b69f transparent;
}

.company-tracking__list:focus-visible { outline: 2px solid #93724a; outline-offset: 3px; }
.company-tracking__list-count { margin: 0; padding: 0.4rem; text-align: center; font-size: 0.75rem; color: #776b5d; }

.company-tracking__item {
  min-width: 0;
  overflow-wrap: anywhere;
  padding: 0.9rem;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.58);
  border: 1px solid rgba(102, 81, 57, 0.08);
}

.company-tracking__item--highlight {
  border-color: rgba(171, 54, 54, 0.32);
  box-shadow: 0 0 0 3px rgba(184, 70, 70, 0.1);
}

.company-tracking__task-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.company-tracking__task-title,
.company-tracking__item-copy,
.company-tracking__item-meta {
  margin: 0;
}

.company-tracking__task-title {
  color: #241c16;
  font-weight: 700;
}

.company-tracking__item-copy {
  margin-top: 0.28rem;
  color: #4a3a2d;
  line-height: 1.45;
}

.company-tracking__item-meta {
  margin-top: 0.36rem;
  color: rgba(45, 35, 26, 0.58);
  font-size: 0.84rem;
}

.company-tracking__status {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  white-space: nowrap;
  padding: 0.2rem 0.5rem;
  border-radius: 999px;
  font-size: 0.65rem;
  line-height: 1.2;
  font-weight: 700;
}

.company-tracking__status--ingresada {
  background: rgba(95, 107, 122, 0.12);
  color: #5a6574;
}

.company-tracking__status--asignada {
  background: rgba(184, 139, 59, 0.14);
  color: #8a6520;
}

.company-tracking__status--trabajando {
  background: rgba(68, 109, 173, 0.14);
  color: #3e649d;
}

.company-tracking__status--terminada {
  background: rgba(77, 140, 97, 0.12);
  color: #2f6a43;
}

.company-tracking__actions--task {
  margin-top: 0.7rem;
}

.company-tracking__empty,
.company-tracking__error {
  margin: 0;
}

.company-tracking__empty {
  color: rgba(45, 35, 26, 0.58);
}

.company-tracking__error {
  margin-bottom: 0.85rem;
  color: #b95252;
}

@media (max-width: 980px) {
  .company-tracking__grid,
  .company-tracking__form-grid {
    grid-template-columns: 1fr;
  }

  .company-tracking__task-head {
    flex-direction: column;
  }
}
</style>
